import { BufferAttribute, BufferGeometry } from 'three';
import { HalfedgeDS } from '@thirdPartyLibraries/three-mesh-halfedge/src';
import { dropCollapsedTriangles } from './dropCollapsedTriangles';

export type SavedGumPreparation = {
  /** Collapsed triangles removed; 0 when the fallback restored them as slivers. */
  collapsedDropped: number;
  /** Coincident vertices moved apart so the position weld leaves them alone. */
  coincidentNudged: number;
};

/**
 * Makes a saved correction's gum PLY buildable as a half-edge structure.
 *
 * The designer snaps gum seam vertices onto crown vertices, so a saved gum can
 * carry vertices at bit-identical positions. Reloading welds those by position
 * (see setFromGeometry), and the weld can produce topology addEdge rejects —
 * mid-build, inside a constructor that catches the throw, so the export runs on
 * a half-built structure. Two shapes of it:
 *
 *  - the pair shares a triangle → that triangle collapses to a zero-area edge
 *    ("Vertices v1 and v2 should be different"); dropCollapsedTriangles handles
 *    this and is validated on real cases, so it stays the first choice;
 *  - the pair does NOT share a triangle → the weld pinches two separate fans
 *    into one bowtie vertex, and once one fan closes, the other's edges find no
 *    free gap ("Vertices v1 and v2 are not free"). No triangle drop fixes that
 *    — dropping can itself cut a fan into two arcs and CREATE the bowtie.
 *
 * When the welded mesh does not build, this falls back to the one topology that
 * is manifold by construction: the PLY's own index order, written from the
 * designer's live half-edge structure. The dropped triangles are restored and
 * every would-be-welded vertex is nudged apart by a few float32 ULPs
 * (nanometres — far below print resolution), so the weld finds nothing to merge
 * and the reload reproduces the saved structure exactly, zero-area seam
 * triangles included.
 *
 * Mutates `geometry` (index and/or positions) and returns what it did.
 * Throws when neither route yields a complete structure — better a loud load
 * error than a silently half-built gum.
 */
export function prepareSavedGumGeometry(geometry: BufferGeometry, tolerance = 1e-10): SavedGumPreparation {
  const indexBeforeDrop = geometry.getIndex();
  const collapsedDropped = dropCollapsedTriangles(geometry, tolerance);
  if (buildsCompletely(geometry, tolerance)) {
    return { collapsedDropped, coincidentNudged: 0 };
  }

  if (collapsedDropped > 0 && indexBeforeDrop) {
    geometry.setIndex(indexBeforeDrop);
  }
  const coincidentNudged = separateCoincidentVertices(geometry, tolerance);
  if (!buildsCompletely(geometry, tolerance)) {
    throw new Error(
      'prepareSavedGumGeometry: the gum does not build a half-edge structure even with its own ' +
        'PLY topology — the saved mesh itself is non-manifold',
    );
  }
  return { collapsedDropped: 0, coincidentNudged };
}

/**
 * True when a fresh structure builds without throwing AND holds every triangle.
 * The face-count check matters: addFace refuses a face whose halfedges are
 * taken (duplicate faces after a weld) by returning false, not by throwing, so
 * "did not throw" alone still lets a silently incomplete gum through.
 */
function buildsCompletely(geometry: BufferGeometry, tolerance: number): boolean {
  const index = geometry.getIndex();
  const expectedFaces = (index ? index.count : geometry.getAttribute('position').count) / 3;
  const probe = new HalfedgeDS();
  try {
    probe.setFromGeometry(geometry, tolerance);
  } catch {
    return false;
  }
  return probe.faces.length === expectedFaces;
}

/**
 * Moves every vertex that would weld onto an earlier one just far enough apart
 * that computeVerticesIndexArray keeps them distinct, and returns how many
 * moved. Reproduces that function's hashing exactly, so the criterion cannot
 * drift from the one that decides what welds.
 */
export function separateCoincidentVertices(geometry: BufferGeometry, tolerance = 1e-10): number {
  const positions = geometry.getAttribute('position') as BufferAttribute;
  const array = positions.array as Float32Array;
  const shiftMultiplier = Math.pow(10, Math.log10(1 / tolerance));
  const hashOf = (vertex: number) => {
    let hash = '';
    for (let j = 0; j < 3; j++) {
      hash += `${Math.round(array[vertex * 3 + j] * shiftMultiplier)}`;
    }
    return hash;
  };

  const seen = new Set<string>();
  let nudged = 0;

  for (let i = 0; i < positions.count; i++) {
    let hash = hashOf(i);
    if (!seen.has(hash)) {
      seen.add(hash);
      continue;
    }

    // Walk the largest-magnitude coordinate through evenly spaced candidates
    // until the hash lands on a free slot. The step must clear two hurdles at
    // once: survive float32 rounding (≥ ~2 ULPs of the value, the relative
    // term) and move the rounded hash by at least a full quantum of the weld
    // tolerance (the absolute term, which carries coordinates near zero).
    let axis = 0;
    for (let j = 1; j < 3; j++) {
      if (Math.abs(array[i * 3 + j]) > Math.abs(array[i * 3 + axis])) axis = j;
    }
    const value = array[i * 3 + axis];
    const step = Math.max(Math.abs(value) * 2.4e-7, 2 / shiftMultiplier);
    let k = 1;
    do {
      array[i * 3 + axis] = Math.fround(value + k * step);
      k++;
      hash = hashOf(i);
    } while (seen.has(hash) || array[i * 3 + axis] === value);
    seen.add(hash);
    nudged++;
  }

  if (nudged > 0) positions.needsUpdate = true;
  return nudged;
}
